import {
  useModalContext,
  useWSSnackbar,
  WSButton,
  WSDrawer,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSLayout,
  WSMessageBox,
  WSPanel,
  WSScreen,
  WSText
} from "@wingspanhq/fe-component-library";

import {
  DeductionStatus,
  ICollaboratorSchema,
  MemberClientStatus,
  WSDataTypes
} from "@wingspanhq/payments/dist/interfaces";
import orderBy from "lodash/orderBy";
import React, { useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { WSPersistentUpgradeButton } from "../../components/Membership/WSPersistentUpgradeButton";
import { OverlaySpinner } from "../../components/OverlaySpinner";
import { createPPL } from "../../PersonalPayLink/utils";
import { useFeatureFlags } from "../../query/hooks/useFeatureFlags";
import {
  useIntegrationsQuickbooks,
  useIntegrationsQuickbooksAccountExpenses,
  useIntegrationsQuickbooksVendors
} from "../../query/integrations/queries";
import { isQuickbooksServiceActive } from "../../query/integrations/selectors";
import {
  useDownloadCollaborator1099,
  useSendCollaboratorInvite,
  useUpdateCollaborator
} from "../../query/payments/mutations";
import {
  useCollaboratorGroupsQuery,
  useCollaboratorQuery,
  useListCollaboratorSettingsAdditionalData
} from "../../query/payments/queries";
import {
  getCollaboratorCompany,
  getCollaboratorEmail,
  getCollaboratorName
} from "../../query/payments/selectors";
import { WSQueries } from "../../query/WSQuery";
import { getIntegrationUserSelectLabel } from "../../Settings/screens/Integrations/quickbooks/QuickbooksSetupStep6";
import { WSFrontendFeature } from "../../Settings/utils/subscriptionUtils";
import { openInNewTab } from "../../utils/openInNewTab";
import { CollaboratorOnboardingRequirements } from "../components/CollaboratorOnboardingRequirements/CollaboratorOnboardingRequirements";
import {
  COLLABORATOR_TAX_INFO_MODAL,
  CollaboratorTaxInfoModal
} from "../components/CollaboratorTaxInfoModal/CollaboratorTaxInfoModal";
import {
  EDIT_COLLABORATOR_ADDITIONAL_INFO_MODAL,
  EditCollaboratorAdditionalInfoModal
} from "../components/EditCollaboratorAdditionalInfoModal/EditCollaboratorAdditionalInfoModal";
import {
  EDIT_COLLABORATOR_INTEGRATIONS_MODAL,
  EditCollaboratorIntegrationsModal
} from "../components/EditCollaboratorIntegrationsModal/EditCollaboratorIntegrationsModal";
import { QuickbooksHistory } from "../components/QuickbooksHistory/QuickbooksHistory";
import {
  ADD_COLLABORATOR_TO_GROUP_MODAL,
  AddCollaboratorToGroupModal
} from "./collaborators/AddCollaboratorToGroupModal";
import styles from "./InvoicesContactDetails.module.scss";
import { CollaboratorDeductionsList } from "../components/Deductions/DeductionsList";
import { getParentPath } from "../../utils/goToParentRoute";
import { useUserId } from "../../query/hooks/helpers";
import { useUserProfile } from "../../query/users/queries";
import { UserAccountType } from "@wingspanhq/users/dist/lib/interfaces";
import DocumentsSection from "../components/DocumentsSection";
import getDocumentsMetaFromProfile from "../components/DocumentsSection/getDocumentsMetaFromProfile";
import { TaxFormStatusBadge } from "../../1099NECFiling/components/TaxFormStatusBadge";

type Props = RouteComponentProps<
  { collaboratorId: string },
  {},
  { backPath: string }
>;

export const InvoicesCollaboratorDetails: React.FC<Props> = ({
  match,
  location
}) => {
  const history = useHistory();
  const backPath = location.state?.backPath || getParentPath(history);
  const collaboratorQuery = useCollaboratorQuery(match.params.collaboratorId);
  const qGroups = useCollaboratorGroupsQuery();

  return (
    <WSQueries
      queries={{ collaboratorQuery, qGroups }}
      renderLoader={() => (
        <>
          <WSScreen.Mobile>
            <OverlaySpinner />
          </WSScreen.Mobile>
          <WSScreen.TabletAndDesktop>
            <WSDrawer
              onClose={() => {
                history.push(backPath);
              }}
              size="S"
              noCloseIcon
            >
              <WSElement className={styles.main}>
                <OverlaySpinner />
              </WSElement>
            </WSDrawer>
          </WSScreen.TabletAndDesktop>
        </>
      )}
    >
      {({ collaboratorQuery: { data: collaborator } }) => (
        <>
          <WSScreen.Mobile>
            <WSLayout
              title="Collaborator Profile"
              onBack={() => {
                history.push(backPath);
              }}
              line
            >
              <WSElement className={styles.main}>
                <Inner collaborator={collaborator} backPath={backPath} />
              </WSElement>
            </WSLayout>
          </WSScreen.Mobile>
          <WSScreen.TabletAndDesktop>
            <WSDrawer
              onClose={() => {
                history.push(backPath);
              }}
              size="S"
              noCloseIcon
            >
              <WSLayout
                title="Collaborator Profile"
                onClose={() => {
                  history.push(backPath);
                }}
                line
                smallHeader
              >
                <WSElement className={styles.main}>
                  <Inner collaborator={collaborator} backPath={backPath} />
                </WSElement>
              </WSLayout>
            </WSDrawer>
          </WSScreen.TabletAndDesktop>
        </>
      )}
    </WSQueries>
  );
};

const Inner: React.FC<{
  collaborator: ICollaboratorSchema;
  backPath: string;
}> = ({ collaborator, backPath }) => {
  const history = useHistory();
  const [updateCollaborator, updateCollaboratorMeta] = useUpdateCollaborator(
    collaborator.collaboratorId
  );
  const userId = useUserId();
  const userQuery = useUserProfile(userId);
  const user = userQuery.data;
  const { createdBy, contractorId, ...additionalData } =
    collaborator?.labels || {};
  const { openModal } = useModalContext();
  const company = getCollaboratorCompany(collaborator);
  const name = getCollaboratorName(collaborator);
  const { openSnackbar } = useWSSnackbar();
  const [sendCollaboratorInvite] = useSendCollaboratorInvite();
  const [
    downloadCollaborator1099,
    downloadCollaborator1099Meta
  ] = useDownloadCollaborator1099();

  const collaboratorSettingsAdditionalDataQuery = useListCollaboratorSettingsAdditionalData();
  const collaboratorSettingsAdditionalData =
    collaboratorSettingsAdditionalDataQuery.data || [];

  const qGroups = useCollaboratorGroupsQuery();
  const qFeatureFlags = useFeatureFlags();

  const qIntegrationsQuickbooks = useIntegrationsQuickbooks();
  const qIntegrationsQuickbooksVendors = useIntegrationsQuickbooksVendors();
  const qIntegrationsQuickbooksExpenseAccounts = useIntegrationsQuickbooksAccountExpenses();

  const integrationState = qIntegrationsQuickbooks.data;
  const integrationQuickbooksVendors =
    qIntegrationsQuickbooksVendors.data || [];
  const integrationQuickbooksExpenseAccounts =
    qIntegrationsQuickbooksExpenseAccounts.data || [];

  const isQuickbooksActive =
    qFeatureFlags.data?.qboContactsControl &&
    integrationState &&
    isQuickbooksServiceActive(integrationState);

  const defaultAccount = integrationQuickbooksExpenseAccounts.find(
    acct => acct.accountId === integrationState?.defaults?.expenseAccountId
  );

  const mappedVendor = integrationQuickbooksVendors.find(
    vendor => collaborator.integration?.quickbooks?.vendorId === vendor.vendorId
  );
  const mappedExpenseAccount = integrationQuickbooksExpenseAccounts.find(
    acct =>
      acct.accountId === collaborator.integration?.quickbooks?.expenseAccountId
  );
  const collaboratorGroup = (qGroups.data || []).find(
    cg => cg.collaboratorGroupId === collaborator.collaboratorGroupId
  );

  const documents = useMemo(() => getDocumentsMetaFromProfile(collaborator), [
    collaborator
  ]);

  return (
    <>
      <WSPanel noBorder mb="M">
        <WSText.Heading3 mb="S" data-testid="collaboratorName">
          {company || name}
        </WSText.Heading3>

        {company && <WSText.Heading4 mb="M">{name}</WSText.Heading4>}

        {collaborator.status === MemberClientStatus.Pending ? (
          <WSMessageBox.Warning noBorder mt="M">
            <WSFlexBox.Center>
              <WSFlexBox.CenterY mt="S">
                <WSIcon name="warning" size="S" color="yellow500" mr="S" />
                <WSText.Heading4 inline>
                  Not yet eligible for payments
                </WSText.Heading4>
              </WSFlexBox.CenterY>
            </WSFlexBox.Center>
            <WSText.Label mt="M">
              Your invite was sent. We're still waiting for this person to
              complete their onboarding requirements before they can receive
              payments.
            </WSText.Label>
            {/*<WSButton.Warning
              mt="M"
              fullWidth
              onClick={() => {
                sendCollaboratorInvite(collaborator.collaboratorId, {
                  onSuccess: () => {
                    openSnackbar({
                      message: `Invite re-sent to ${collaborator.member.user
                        .profile?.firstName || collaborator.member.user.email}`
                    });
                  }
                });
              }}
            >
              Resend invite
            </WSButton.Warning>*/}
          </WSMessageBox.Warning>
        ) : (
          <WSPersistentUpgradeButton
            feature={WSFrontendFeature.InviteAndPayCollaborator}
            kind="Secondary"
            onClick={() => {
              if (collaborator.member.user.tag) {
                openInNewTab(
                  createPPL(collaborator.member.user.tag, {
                    includeProtocol: true
                  })
                );
              }
            }}
            fullWidth
          >
            Send payment
          </WSPersistentUpgradeButton>
        )}
      </WSPanel>

      <WSPanel noBorder mb="M">
        <WSText.Heading3 mb="S">Collaborator info</WSText.Heading3>

        <WSText.Label mb="XS" color="grey600">
          Email
        </WSText.Label>
        <WSText mb="M">{getCollaboratorEmail(collaborator)}</WSText>

        <WSText.Label mb="XS" color="grey600">
          Group
        </WSText.Label>
        {collaboratorGroup ? (
          <WSText>{collaboratorGroup.name}</WSText>
        ) : (qGroups.data || [])?.length > 0 ? (
          <WSButton.Link
            onClick={() =>
              openModal(ADD_COLLABORATOR_TO_GROUP_MODAL, {
                collaboratorId: collaborator.collaboratorId
              })
            }
          >
            Add to group
          </WSButton.Link>
        ) : (
          <WSText>No collaborator groups exist yet.</WSText>
        )}
      </WSPanel>

      {documents && (
        <DocumentsSection
          documents={documents}
          renderBadge={({ data }) => (
            <TaxFormStatusBadge status={data.status} />
          )}
          testid="documents-section"
        />
      )}

      {qFeatureFlags.data?.collaboratorOnboardingRequirements && (
        <WSPanel noBorder mb="M">
          <CollaboratorOnboardingRequirements
            collaboratorId={collaborator.collaboratorId}
          />
        </WSPanel>
      )}

      {collaboratorSettingsAdditionalData.length > 0 && (
        <WSPanel noBorder mb="M">
          <EditCollaboratorAdditionalInfoModal
            collaboratorId={collaborator.collaboratorId}
          />
          <WSFlexBox wrap="nowrap" justify="space-between">
            <WSText.Heading3 mb="S">Custom fields</WSText.Heading3>
            <WSButton.Link
              type="button"
              onClick={() => {
                openModal(EDIT_COLLABORATOR_ADDITIONAL_INFO_MODAL);
              }}
            >
              Edit
            </WSButton.Link>
          </WSFlexBox>
          {user?.settings?.userAccountType === UserAccountType.enterprise &&
          contractorId ? (
            <WSElement mb="S">
              <WSText.Label color="grey600">
                External Collaborator ID
              </WSText.Label>
              <WSText>{contractorId}</WSText>
            </WSElement>
          ) : null}
          {orderBy(collaboratorSettingsAdditionalData, "type", "desc").map(
            field => {
              return (
                <WSElement mb="S">
                  <WSText.Label color="grey600">{field.name}</WSText.Label>
                  <WSText>
                    {field.type === WSDataTypes.Boolean
                      ? additionalData[field.key] === "true"
                        ? "Yes"
                        : "No"
                      : additionalData[field.key] || "-"}
                  </WSText>
                </WSElement>
              );
            }
          )}
        </WSPanel>
      )}

      {collaborator.form1099Balances?.["2021"] && (
        <WSPanel noBorder mb="M">
          <CollaboratorTaxInfoModal
            collaboratorId={collaborator.collaboratorId}
          />
          <WSFlexBox wrap="nowrap" justify="space-between">
            <WSText.Heading3 mb="S">Tax information 2021</WSText.Heading3>
            <WSButton.Link
              type="button"
              onClick={() => {
                openModal(COLLABORATOR_TAX_INFO_MODAL);
              }}
            >
              Edit
            </WSButton.Link>
          </WSFlexBox>

          <WSElement mb="S">
            <WSText.Label mb="XS" color="grey600">
              On Platform Gross Income
            </WSText.Label>
            <WSText formatMoney>
              {collaborator.form1099Balances["2021"].platformIncome}
            </WSText>
          </WSElement>

          <WSElement mb="S">
            <WSText.Label mb="XS" color="grey600">
              Off Platform Adjustments
            </WSText.Label>
            <WSText formatMoney>
              {collaborator.form1099Balances["2021"].adjustments}
            </WSText>
          </WSElement>

          <WSElement mb="S">
            <WSText.Label mb="XS" color="grey600">
              2021 Non-employee Compensation
            </WSText.Label>
            <WSText formatMoney>
              {(collaborator.form1099Balances["2021"].platformIncome || 0) +
                (collaborator.form1099Balances["2021"].adjustments || 0)}
            </WSText>
          </WSElement>

          <WSElement mb="S">
            <WSText.Label mb="XS" color="grey600">
              Send 1099 Filing
            </WSText.Label>
            <WSText>
              {(collaborator.form1099Balances["2021"] as any).issue1099
                ? "Yes"
                : "No"}
            </WSText>
          </WSElement>

          {collaborator.form1099Balances["2021"].form1099Generated && (
            <WSButton.Link
              mt="M"
              icon="download"
              onClick={() => {
                downloadCollaborator1099({
                  collaboratorId: collaborator.collaboratorId,
                  collaborator
                });
              }}
              loading={downloadCollaborator1099Meta.isLoading}
            >
              Download 1099
            </WSButton.Link>
          )}
        </WSPanel>
      )}

      <CollaboratorDeductionsList
        collaboratorId={collaborator.collaboratorId}
        clientId={collaborator.clientId}
        memberId={collaborator.memberId}
        onRowClick={d => {
          if (d.status === DeductionStatus.Pending) {
            history.push(
              `${history.location.pathname}/deduction/scheduled/${d.deductionId}?${history.location.search}`
            );
          } else {
            history.push(
              `${history.location.pathname}/deduction/completed/${d.deductionId}?${history.location.search}`
            );
          }
        }}
      />

      {isQuickbooksActive && (
        <>
          <EditCollaboratorIntegrationsModal
            collaboratorId={collaborator.collaboratorId}
          />
          <WSPanel noBorder mb="M" data-testid="collaboratorIntegrationMapping">
            <WSFlexBox.CenterY wrap="nowrap" justify="space-between" mb="S">
              <WSText.Heading3>Integrations Mappings</WSText.Heading3>
              <WSButton.Link
                onClick={() => {
                  openModal(EDIT_COLLABORATOR_INTEGRATIONS_MODAL);
                }}
              >
                Edit
              </WSButton.Link>
            </WSFlexBox.CenterY>

            <WSText.Label mb="XS" color="grey600">
              QBO Vendor
            </WSText.Label>
            <WSText mb="S">
              {mappedVendor
                ? getIntegrationUserSelectLabel(mappedVendor)
                : "Not mapped"}
            </WSText>
            <WSText.Label mb="XS" color="grey600">
              Default QBO Expense Account
            </WSText.Label>
            <WSText>
              {mappedExpenseAccount
                ? mappedExpenseAccount.fullyQualifiedName
                : `Not mapped, use default "${defaultAccount?.fullyQualifiedName}"`}
            </WSText>
          </WSPanel>

          <QuickbooksHistory
            Wrapper={WSPanel}
            noBorder
            mb="M"
            entityId={collaborator.collaboratorId}
          />
        </>
      )}

      <WSPanel noBorder>
        <WSButton.Link
          name="deleteCollaborator"
          color="red500"
          onClick={() => {
            updateCollaborator(
              {
                status: MemberClientStatus.Inactive
              },
              {
                onSuccess: () => {
                  history.push(backPath);
                }
              }
            );
          }}
          loading={updateCollaboratorMeta.isLoading}
        >
          Delete collaborator
        </WSButton.Link>
      </WSPanel>
      <AddCollaboratorToGroupModal />
    </>
  );
};
